import { useId, useRef, useState, type FormEvent, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { THREE_PL_BILL_NOTE_MAX, THREE_PL_BILL_QTY_MAX, toCents } from '@threadheads/shared';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '@/hooks/use-toast';
import { trpcErrorCode } from '@/lib/trpc-error-utils';
import { cn } from '@/lib/utils';
import type { useThreePlBillLines } from '../hooks/useReportingV2';
import {
  billQtyCanBecomeValid,
  formatUsageDay,
  isDayInPeriod,
  localToday,
  parseBillNote,
  parseBillQty,
  type ThreePlBillService,
} from '../lib/three-pl-usage-view';

type AddLineMutation = ReturnType<typeof useThreePlBillLines>['addLine'];

const QTY_LABEL_KEYS: Record<ThreePlBillService['chargePer'], string> = {
  piece: 'reporting.threePlUsage.addToBill.qty.piece',
  order: 'reporting.threePlUsage.addToBill.qty.order',
  flat: 'reporting.threePlUsage.addToBill.qty.flat',
};

const PAYS_KEYS: Record<ThreePlBillService['chargePer'], string> = {
  piece: 'reporting.threePlUsage.addToBill.hint.piece',
  order: 'reporting.threePlUsage.addToBill.hint.order',
  flat: 'reporting.threePlUsage.addToBill.hint.flat',
};

/** A flat fee is charged once per add unless the 3PL says otherwise (D8). */
const defaultQty = (service: ThreePlBillService | undefined) => (service?.chargePer === 'flat' ? '1' : '');

export interface AddToBillDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  client: { id: string; name: string };
  locationId: string;
  /** This client's entries of the owner-only `billServices`: the 3PL's own active services, never the built-ins. */
  services: readonly ThreePlBillService[];
  /** The location's zone (`report.location.timezone`): the line bills on its today (D3, D27). */
  timeZone: string;
  /** The period on screen: a line billed outside it will not show on this bill. */
  period: { from: string; to: string };
  /** Formats in the report's currency (D2). */
  formatValue: (value: number) => string;
  /** The tab's mutation (useThreePlBillLines): one call, then one report refetch. */
  addLine: AddLineMutation;
  /** After the one call succeeded, with the day the line bills on (stamped in SQL). */
  onAdded: (result: { chargeDate: string }) => void;
  onCloseAutoFocus?: (event: Event) => void;
}

/**
 * Add a service to this bill (MOCKUP §3, owner `skus.costs_manage` holders
 * only): one of the 3PL's own services, how many, and an optional note the
 * client sees (D36). The price sits under the service, and under the quantity
 * one slot holds either the live "Qty × Price = Amount" (a price above 0 only)
 * or the quantity's error, so typing never moves the dialog. ONE
 * `addThreePlBillLine` call; the line bills on the location's today, stamped
 * in SQL (D3), and the dialog says which day before the add (D27). Errors sit
 * under their field (§15.3); while the call runs every input is disabled and
 * the dialog cannot be closed. The form remounts on every open, so each
 * opening starts clean.
 */
export function AddToBillDialog({
  open,
  onOpenChange,
  client,
  locationId,
  services,
  timeZone,
  period,
  formatValue,
  addLine,
  onAdded,
  onCloseAutoFocus,
}: AddToBillDialogProps) {
  const { t } = useTranslation('inventory');
  const qtyRef = useRef<HTMLInputElement>(null);
  const busy = addLine.isPending;
  const handleOpenChange = (next: boolean) => {
    if (!next && busy) return;
    onOpenChange(next);
  };
  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-h-[85vh] overflow-y-auto sm:max-w-md"
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          qtyRef.current?.focus();
        }}
        onCloseAutoFocus={onCloseAutoFocus}
      >
        <DialogHeader>
          <DialogTitle className="[overflow-wrap:anywhere]">
            {t('reporting.threePlUsage.addToBill.title', { client: client.name })}
          </DialogTitle>
        </DialogHeader>
        <AddToBillForm
          client={client}
          locationId={locationId}
          services={services}
          timeZone={timeZone}
          period={period}
          formatValue={formatValue}
          addLine={addLine}
          qtyRef={qtyRef}
          onCancel={() => handleOpenChange(false)}
          onAdded={onAdded}
        />
      </DialogContent>
    </Dialog>
  );
}

function AddToBillForm({
  client,
  locationId,
  services,
  timeZone,
  period,
  formatValue,
  addLine,
  qtyRef,
  onCancel,
  onAdded,
}: Omit<AddToBillDialogProps, 'open' | 'onOpenChange' | 'onCloseAutoFocus'> & {
  qtyRef: RefObject<HTMLInputElement>;
  onCancel: () => void;
}) {
  const { t, i18n } = useTranslation('inventory');
  const ids = {
    service: useId(),
    qty: useId(),
    /** One slot under Qty: the error, else the Qty × Price math, else empty. */
    qtyNote: useId(),
    note: useId(),
    noteError: useId(),
    price: useId(),
  };
  const noteRef = useRef<HTMLInputElement>(null);
  const [serviceId, setServiceId] = useState(() => services[0]?.id ?? '');
  const [qty, setQty] = useState(() => defaultQty(services[0]));
  const [qtyEdited, setQtyEdited] = useState(false);
  const [qtyCommitted, setQtyCommitted] = useState(false);
  const [note, setNote] = useState('');
  const [submitted, setSubmitted] = useState(false);
  // The day this opening would bill on; the toast names the day SQL stamped.
  const [today] = useState(() => localToday(timeZone, new Date()));
  const busy = addLine.isPending;

  // A refetch can drop the picked service (removed on Client prices): fall
  // back to the first one still offered rather than send a stale id.
  const service = services.find((entry) => entry.id === serviceId) ?? services[0];

  const parsedQty = parseBillQty(qty);
  const qtyBad = parsedQty.kind !== 'ok';
  const showQtyError = qtyBad && (submitted || qtyCommitted || !billQtyCanBecomeValid(qty));
  const parsedNote = parseBillNote(note);
  const showNoteError = parsedNote.kind === 'tooLong';

  const pickService = (id: string) => {
    if (busy) return;
    setServiceId(id);
    // A quantity nobody typed follows the charge type: a flat fee starts at 1.
    if (!qtyEdited) setQty(defaultQty(services.find((entry) => entry.id === id)));
  };

  const submit = (event?: FormEvent) => {
    event?.preventDefault();
    if (busy || !service) return;
    setSubmitted(true);
    if (parsedQty.kind !== 'ok') {
      qtyRef.current?.focus();
      return;
    }
    if (parsedNote.kind !== 'ok') {
      noteRef.current?.focus();
      return;
    }
    addLine.mutate(
      {
        locationId,
        clientOrgId: client.id,
        serviceId: service.id,
        qty: parsedQty.value,
        note: parsedNote.value,
      },
      {
        onSuccess: (result) => onAdded({ chargeDate: result.chargeDate }),
        onError: (error) => {
          const code = trpcErrorCode(error);
          const title =
            code === 'NOT_FOUND'
              ? t('reporting.threePlUsage.addToBill.errors.notFound')
              : code === 'BAD_REQUEST'
                ? t('reporting.threePlUsage.addToBill.errors.badRequest')
                : t('reporting.threePlUsage.toast.addError');
          toast({ variant: 'destructive', title, duration: Infinity });
        },
      },
    );
  };

  const language = i18n.language;
  const day = formatUsageDay(today, language);
  const priceHint =
    !service || service.priceToday === null
      ? t('reporting.threePlUsage.addToBill.hint.noPrice', { client: client.name })
      : t(PAYS_KEYS[service.chargePer], { client: client.name, price: formatValue(service.priceToday) });
  const qtyLabel = t(QTY_LABEL_KEYS[service?.chargePer ?? 'piece']);
  // Only a price above 0: a 0 price bills a muted "-" Amount, so "= $0.00"
  // would disagree with the bill (the sentence under Service names the price).
  // Integer cents times an integer qty is exact.
  const priceToday = service?.priceToday ?? 0;
  const qtyMath =
    parsedQty.kind === 'ok' && priceToday > 0
      ? t('reporting.threePlUsage.addToBill.math', {
          qty: parsedQty.value,
          price: formatValue(priceToday),
          amount: formatValue((toCents(priceToday) * parsedQty.value) / 100),
        })
      : null;

  return (
    <form onSubmit={submit} className="grid gap-4" noValidate>
      <div className="space-y-1.5">
        <Label htmlFor={ids.service}>{t('reporting.threePlUsage.addToBill.service')}</Label>
        <Select value={service?.id ?? ''} onValueChange={pickService} disabled={busy}>
          <SelectTrigger id={ids.service} className="h-9" aria-describedby={ids.price}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {services.map((entry) => (
              <SelectItem key={entry.id} value={entry.id} className="[overflow-wrap:anywhere]">
                {entry.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {/* Polite: picking another service reads its price out. */}
        <p id={ids.price} aria-live="polite" className="text-xs text-muted-foreground [overflow-wrap:anywhere]">
          {priceHint}
        </p>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor={ids.qty}>{qtyLabel}</Label>
        <Input
          ref={qtyRef}
          id={ids.qty}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          spellCheck={false}
          value={qty}
          placeholder={t('reporting.threePlUsage.addToBill.qtyPlaceholder')}
          disabled={busy}
          aria-invalid={showQtyError || undefined}
          aria-describedby={ids.qtyNote}
          className={cn('w-28 text-right tabular-nums', showQtyError && 'border-destructive ring-1 ring-destructive')}
          onChange={(event) => {
            setQty(event.target.value);
            setQtyEdited(true);
            setQtyCommitted(false);
          }}
          onBlur={() => setQtyCommitted(true)}
        />
        {/* One slot (§9, §15.3): typing, erroring and recovering never move the
            dialog. Not a live region: it would speak every keystroke. The
            leading comes last so tailwind-merge keeps it over the font size,
            and the slot is a div because the zh stylesheet sets every `p` to
            line-height 1.6, which would grow the text-sm error by 2.4 px. */}
        <div
          id={ids.qtyNote}
          className={cn(
            'min-h-5',
            showQtyError ? 'text-sm text-destructive' : 'text-xs text-muted-foreground tabular-nums',
            'leading-5',
          )}
        >
          {showQtyError
            ? parsedQty.kind === 'tooMany'
              ? t('reporting.threePlUsage.addToBill.errors.qtyMax', { max: THREE_PL_BILL_QTY_MAX })
              : t('reporting.threePlUsage.addToBill.errors.qty')
            : qtyMath}
        </div>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor={ids.note}>{t('reporting.threePlUsage.addToBill.note')}</Label>
        <Input
          ref={noteRef}
          id={ids.note}
          value={note}
          placeholder={t('reporting.threePlUsage.addToBill.notePlaceholder')}
          autoComplete="off"
          disabled={busy}
          aria-invalid={showNoteError || undefined}
          aria-describedby={showNoteError ? ids.noteError : undefined}
          className={cn(showNoteError && 'border-destructive ring-1 ring-destructive')}
          onChange={(event) => setNote(event.target.value)}
        />
        {showNoteError && (
          <p id={ids.noteError} className="text-sm text-destructive">
            {t('reporting.threePlUsage.addToBill.errors.note', { max: THREE_PL_BILL_NOTE_MAX })}
          </p>
        )}
      </div>

      {/* What the add does: who sees the line (D36), the day it bills on (D27).
          The price sits under the service it belongs to. */}
      <DialogDescription asChild className="space-y-1 text-xs [overflow-wrap:anywhere]">
        <div>
          <p>{t('reporting.threePlUsage.addToBill.hint.sees', { client: client.name })}</p>
          <p>
            {isDayInPeriod(today, period)
              ? t('reporting.threePlUsage.addToBill.hint.billedOn', { date: day })
              : t('reporting.threePlUsage.addToBill.hint.outsidePeriod', { date: day })}
          </p>
        </div>
      </DialogDescription>

      <DialogFooter>
        <Button type="button" variant="outline" className="h-8 px-3 text-xs" onClick={onCancel} disabled={busy}>
          {t('reporting.threePlUsage.addToBill.cancel')}
        </Button>
        <Button
          type="submit"
          className="h-8 gap-1.5 bg-threadheads-purple px-3 text-xs text-white hover:bg-threadheads-purple/90"
          disabled={busy || !service}
          aria-busy={busy}
        >
          {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />}
          {t('reporting.threePlUsage.addToBill.submit')}
        </Button>
      </DialogFooter>
    </form>
  );
}
